/**
 * SALESmanago — reguły wspólne dla trzech warstw, które muszą się zgadzać:
 *  1. klient (`app/composables/useSalesmanago.ts`) — ładuje sm.js,
 *  2. CSP (`server/utils/salesmanagoCsp.ts`) — wpuszcza host konta SM,
 *  3. trasa Nitro (`server/api/store/salesmanago/identity.get.ts`) — oddaje ID kontaktu.
 *
 * Gdyby allowlista marek albo postać hosta rozjechały się między nimi, sm.js
 * ładowałby się na sklepie, którego CSP go nie wpuszcza (albo odwrotnie: CSP
 * luzowane bez integracji). Dlatego jedno miejsce.
 */

/**
 * Brandy (== NUXT_PUBLIC_BRAND), na których działa SALESmanago. Dopisanie sklepu =
 * jego `id` tutaj — loader i CSP czytają tę samą listę. Samo włączenie robi env
 * (NUXT_PUBLIC_SALESMANAGO_CLIENT_ID + NUXT_PUBLIC_SALESMANAGO_ENDPOINT).
 */
export const SALESMANAGO_BRANDS = new Set<string>(['lavmag'])

export function isSalesmanagoBrand(brand: unknown): boolean {
  return typeof brand === 'string' && SALESMANAGO_BRANDS.has(brand)
}

const HOST_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/

/**
 * Host konta SALESmanago (np. `app2.salesmanago.pl`) z env
 * NUXT_PUBLIC_SALESMANAGO_ENDPOINT. Env ma zawierać sam host, ale tolerujemy adres
 * wklejony z panelu (`https://app2.salesmanago.pl/`) — schemat i ścieżkę obcinamy.
 *
 * Wszystko, co po obcięciu nie jest nazwą DNS (spacje, `*`, port, znaki spoza
 * alfabetu hosta), daje `null` = integracja wyłączona. Literówka w inventory nie
 * może wpuścić do CSP niczego szerszego niż jeden konkretny host.
 */
export function normalizeSalesmanagoHost(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const host = value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase()

  return HOST_PATTERN.test(host) ? host : null
}

/** Klucze runtimeConfig.public, od których zależy włączenie integracji. */
export interface SalesmanagoPublicConfig {
  brand?: unknown
  salesmanagoClientId?: unknown
  salesmanagoEndpoint?: unknown
}

/**
 * Jeden warunek „SALESmanago włączony” dla loadera, banera zgody, CSP i trasy
 * tożsamości: client ID + poprawny host konta + brand z allowlisty. Gdyby baner
 * pytał o zgodę przy samym client ID, a loader odpuszczał (np. literówka w
 * endpoincie), pytalibyśmy o zgodę na coś, czego nie robimy.
 *
 * `String()`: runtime'owe nadpisanie env przechodzi przez destr, więc client ID
 * złożony z samych cyfr potrafi przyjść jako liczba.
 */
export function isSalesmanagoEnabled(config: SalesmanagoPublicConfig): boolean {
  return Boolean(
    String(config.salesmanagoClientId ?? '').trim()
    && normalizeSalesmanagoHost(config.salesmanagoEndpoint)
    && isSalesmanagoBrand(config.brand),
  )
}

/**
 * Domeny, na których mogło zostać zapisane ciasteczko SM: sam host oraz każda
 * domena nadrzędna (sm.js zapisuje na domenie nadrzędnej, np. `.lavmag.eu`).
 * Do kasowania — przeglądarka usuwa ciasteczko tylko przy zgodnym `domain`.
 * Bez samego TLD; dla `localhost` i adresów IP pusto (zostaje wariant host-only).
 */
export function salesmanagoCookieDomains(hostname: string): string[] {
  if (!hostname || /^[\d.]+$/.test(hostname) || hostname.includes(':')) return []
  const labels = hostname.toLowerCase().split('.').filter(Boolean)
  const domains: string[] = []
  for (let index = 0; index <= labels.length - 2; index++) {
    domains.push(labels.slice(index).join('.'))
  }
  return domains
}

/** Odpowiedź `GET /api/store/salesmanago/identity`. */
export interface SalesmanagoIdentity {
  contactId: string | null
}

const CONTACT_ID_PATTERN = /^[A-Za-z0-9-]{1,64}$/

/**
 * Czyta `contactId` z odpowiedzi tożsamości (backendu albo trasy Nitro).
 *
 * ID kontaktu SM to UUID, backend trzyma je w VARCHAR(64). Wartość trafia do
 * zmiennej globalnej `_smclientid` i ciasteczka `smclient`, więc przepuszczamy
 * wyłącznie `[A-Za-z0-9-]{1,64}` — cokolwiek innego traktujemy jak brak kontaktu.
 */
export function readSalesmanagoContactId(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null
  return validSalesmanagoContactId((payload as { contactId?: unknown }).contactId)
}

/** Sama walidacja wartości ID kontaktu (zob. {@link readSalesmanagoContactId}). */
export function validSalesmanagoContactId(value: unknown): string | null {
  return typeof value === 'string' && CONTACT_ID_PATTERN.test(value) ? value : null
}
